







































































































































































































import { computed, defineComponent, PropType, ref, watch } from '@vue/composition-api';
import { FilePathDisplay, FileWithIcon, FormBlock, FormCard, TwButton, TwProgressBar } from '@/app/components';
import { useApolloTask, useHarvester, useRunnerPath } from '../../../composable';
import { FileType, WorkflowStatus } from '../../../constants';
import { ApolloTask, FileHarvesterConfiguration, FilesData } from '../../../types';
import { FileDropTarget, SampleUpload } from '../common';
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';
import * as R from 'ramda';
import { parseString, processors } from 'xml2js';
import { useFilters } from '@/app/composable';
import { ChevronRightIcon } from '@vue-hero-icons/outline';
import { QuestionMarkCircleIcon } from '@vue-hero-icons/solid';
import { localRelativePathValidator } from '@/app/validators';

extend('relativePath', localRelativePathValidator);

export default defineComponent({
    name: 'SetupFileHarvester',
    components: {
        FileDropTarget,
        FormBlock,
        FormCard,
        ValidationObserver,
        ValidationProvider,
        SampleUpload,
        FileWithIcon,
        TwButton,
        ChevronRightIcon,
        TwProgressBar,
        FilePathDisplay,
        QuestionMarkCircleIcon,
    },
    model: {
        prop: 'task',
        event: 'update-task',
    },
    props: {
        task: {
            type: Object as PropType<ApolloTask<FileHarvesterConfiguration>>,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        files: {
            type: Object as PropType<FilesData>,
            required: true,
        },
        sample: {
            type: [Array, Object],
            required: false,
        },
    },
    setup(props, { emit, root }) {
        const { formatBytes } = useFilters();
        const filesValidationRef = ref<any>(null);
        const validating = ref<boolean>(false);
        const { inDraftStatus, inUpdateStatus } = useApolloTask(computed(() => props.task));
        const necessaryFields = ref<any>(null);
        const extraInvalidFields = ref<any>(null);
        const { uploadsPath, uploadsPathWidthInPx, buildPath, getRelativePath } = useRunnerPath(
            computed(() => props.task.pipeline.runnerId),
        );
        const relativeFileName = ref<string>(
            getRelativePath(props.task.configuration.inputFilename, uploadsPath.value),
        );

        const fileTypeRef = computed(() => props.task.configuration.fileType);
        const sampleFile = computed(() => props.files.sampleFile);
        const uploadFile = computed(() => props.files.dataFile);
        const allowNext = computed(
            () =>
                props.task.processedSample?.length ||
                (!!props.files.sampleFile &&
                    ((!props.task.pipeline.isOnPremise && !!props.files.dataFile) || props.task.pipeline.isOnPremise)),
        );

        const {
            checkInvalidXML,
            reduceSampleValues,
            invalidFormat,
            rangeError,
            emptyFile,
            valPercentage,
            noCSVData,
            parseCSV,
            acceptedFiles,
            checkInvalidJSON,
            checkInvalidCSV,
            checkInvalidParquet,
            showValidationBar,
        } = useHarvester(root, emit, fileTypeRef, validating);

        const fileBlockInfo = computed(() => {
            if (props.task.canAddFiles && !props.task.pipeline.isOnPremise) {
                return {
                    title: 'Additional files',
                };
            }
            if (props.task.pipeline.isOnPremise)
                return {
                    title: 'File path',
                };
            return {
                title: 'Full dataset file',
            };
        });

        const formatHint = computed(() => {
            const fileType = props.task.configuration.fileType;
            if (!fileType) return null;
            return `${fileType === FileType.Parquet ? 'Parquet' : fileType.toUpperCase()} format expected`;
        });

        const showDatasetUpload = computed(
            () =>
                inUpdateStatus.value ||
                (props.task.configuration.fileType &&
                    (inDraftStatus.value ||
                        props.task.canAddFiles ||
                        props.task.pipeline.status === WorkflowStatus.Ready)),
        );

        const showDatasetBlock = computed(
            () =>
                showDatasetUpload.value ||
                (!inDraftStatus.value &&
                    !inUpdateStatus.value &&
                    (!!props.task.files.length || !!props.task.configuration.inputFilename)),
        );

        const errorAlert: any = ref({
            title: null,
            body: {
                necessary: null,
                invalid: null,
            },
        });

        const setErrorAlert = (value: string | null) => {
            errorAlert.value.title = value;
        };

        const clearErrorAlert = () => {
            errorAlert.value = {
                title: null,
                body: {
                    necessary: null,
                    invalid: null,
                },
            };
        };

        const validate = async () => {
            return filesValidationRef.value.validate();
        };

        const clearActualFile = (resetConfiguration = false) => {
            emit('files-changed', { dataFile: null });
            if (resetConfiguration) {
                props.task.configuration.response = {
                    basePath: 'res',
                    multiple: false,
                    selectedItems: [],
                };
            }
        };

        const getKeys = (obj: any, path = ''): any => {
            if (!obj || typeof obj !== 'object') return path;
            return Object.keys(obj)
                .map((key) => getKeys(obj[key], path ? [path, key].join('.') : key))
                .concat(Number.isNaN(Number(path)) ? [path] : []);
        };

        const getAllPaths = (obj: any) => {
            const allKeys = getKeys(obj).toString().split(',');
            const keys = allKeys.map((key: string) =>
                key
                    .split('.')
                    .filter((keyPart: string) => Number.isNaN(Number(keyPart)))
                    .join('.'),
            );
            return [...new Set(keys)];
        };

        const filetypeValidation = async (filetype: string, file: any) => {
            if (emptyFile.value) {
                clearActualFile();
                (root as any).$toastr.e(`Empty ${filetype} file!`, 'Error');
            } else if (invalidFormat.value) {
                clearActualFile();
                if (!errorAlert.value.title) {
                    (root as any).$toastr.e(`Invalid ${filetype} format!`, 'Error');
                }
            } else if (noCSVData.value) {
                (root as any).$toastr.e('The CSV file contains no data!', 'No Data!');
                clearActualFile();
            } else if (rangeError.value) {
                (root as any).$toastr.e('File is too large or contains multiple nested data', 'Error');
                clearActualFile();
            } else {
                emit('files-changed', { dataFile: file });
            }
        };

        /**
         * Compares the csv headers (columns) of the sample file with the csv headers of the full file to be uploaded.
         * If a header/ column in either csv file (sample or full file) is empty, then in the error displayed to the user,
         * it is written as 'empty column header'
         * @param data CSV text data to be parsed
         */
        const calculateInconsistenciesCSV = async (file: any) => {
            const results = await parseCSV(file, false, 10);
            const headers = results.meta.fields;
            if (!R.isNil(props.sample) && !R.isEmpty(props.sample)) {
                const sampleHeaders = props.task.configuration.params.fields;
                necessaryFields.value = sampleHeaders?.filter((h: any) => headers.indexOf(h) === -1) ?? [];
                necessaryFields.value.forEach((w: any) => {
                    if (!w) {
                        const idx = necessaryFields.value.indexOf(w);
                        necessaryFields.value[idx] = 'empty column header';
                    }
                });

                extraInvalidFields.value = headers.filter((h: any) => sampleHeaders?.indexOf(h) === -1);
                extraInvalidFields.value.forEach((w: any) => {
                    if (!w) {
                        const idx = extraInvalidFields.value.indexOf(w);
                        extraInvalidFields.value[idx] = 'empty column header';
                    }
                });

                // duplicate headers of file are considered as extra invalid fields
                const duplicateHeadersFile = headers.filter(
                    (header: any, idx: number) => headers.indexOf(header) !== idx,
                );
                if (duplicateHeadersFile.length) {
                    extraInvalidFields.value = extraInvalidFields.value.concat(duplicateHeadersFile);
                }
            }
        };

        const calculateFilteredPaths = (paths: any) => {
            const filteredPaths: any = [];
            paths.forEach((p: any) => {
                let pathWithoutSeparator = p.split('||');
                pathWithoutSeparator = pathWithoutSeparator.filter((v: any) => v !== '');
                if (!filteredPaths.find((fp: any) => fp.toString() === pathWithoutSeparator.toString())) {
                    filteredPaths.push(pathWithoutSeparator);
                }
            });
            return filteredPaths;
        };

        /**
         * Compares the structure of the uploaded sample with the full file to be uploaded
         * @param file Full file to be uploaded
         * @param fileType Format of full file to be uploaded (json, xml, csv)
         */
        const compareSampleAndFile = async (file: any, fileType: FileType) => {
            rangeError.value = false;
            let data = null;

            // extract/ parse the text data from the file
            if (fileType === FileType.XML) {
                const addAt = (attrName: string) => {
                    return `@${attrName}`;
                };
                const xmlData = await file.text();
                parseString(
                    xmlData,
                    {
                        attrkey: '@',
                        charkey: '$',
                        explicitCharkey: false,
                        trim: true,
                        emptyTag: {},
                        explicitArray: false,
                        mergeAttrs: true,
                        attrNameProcessors: [addAt],
                        attrValueProcessors: [processors.parseNumbers, processors.parseBooleans],
                        valueProcessors: [processors.parseNumbers, processors.parseBooleans],
                    },
                    (err, result) => {
                        if (!err) {
                            if (R.type(result) === 'Object') {
                                data = [result];
                            } else {
                                data = result;
                            }
                        }
                    },
                );
            } else {
                data = await file.text();
            }

            if (fileType === FileType.CSV) {
                await calculateInconsistenciesCSV(file);
            }

            if ([FileType.JSON, FileType.XML].includes(fileType)) {
                let fileUpload: any = fileType === 'json' ? JSON.parse(data) : data;
                if (props.task.configuration.isSampleCropped) {
                    fileUpload = await reduceSampleValues(fileUpload);
                }

                // remove any paths which are identical
                let invalidPaths = [];

                const paths = getAllPaths(props.sample);
                try {
                    invalidPaths = getAllPaths(fileUpload);
                } catch {
                    rangeError.value = true;
                    return;
                }

                necessaryFields.value = [];
                extraInvalidFields.value = [];

                const filteredPaths = await calculateFilteredPaths(paths);
                const filteredInvalidPaths = await calculateFilteredPaths(invalidPaths);

                // calculate which fields exist in the sample, but not in the full file to be uploaded
                filteredPaths.forEach((fp: any) => {
                    if (!filteredInvalidPaths.find((ip: any) => ip.toString() === fp.toString())) {
                        necessaryFields.value.push(fp);
                    }
                });

                // calculate which fields exist in the full file to be uploaded, but not in the sample
                filteredInvalidPaths.forEach((fp: any) => {
                    if (!filteredPaths.find((ip: any) => ip.toString() === fp.toString())) {
                        extraInvalidFields.value.push(fp);
                    }
                });

                // remove any paths which are identical
                necessaryFields.value = [...new Set(necessaryFields.value)];
                extraInvalidFields.value = [...new Set(extraInvalidFields.value)];
            }

            if (necessaryFields.value && necessaryFields.value.length) {
                invalidFormat.value = true;
                errorAlert.value.title = `Inconsistencies detected between the Sample and the File "${file.name}"`;
                (root as any).$toastr.e('File and Sample do not have the same structure!', 'Error');
            } else if (props.task.configuration.isSampleCropped) {
                invalidFormat.value = false;
                errorAlert.value.title = `Inconsistencies may appear between the Sample and the File "${file.name}", because the Sample has been cropped.`;
            } else {
                invalidFormat.value = false;
                errorAlert.value.title = null;
                errorAlert.value.body = {
                    necessary: null,
                    invalid: null,
                };
            }
        };

        const fileUploaded = async (fileOrEvent: any) => {
            const uploadedFile = fileOrEvent instanceof File ? fileOrEvent : fileOrEvent.target.files[0];
            const filename = uploadedFile.name;
            clearErrorAlert();

            if (!props.task.files.includes(filename)) {
                emit('set-loading', true);

                switch (props.task.configuration.fileType) {
                    case FileType.JSON: {
                        await checkInvalidJSON(uploadedFile);

                        if (!invalidFormat.value && !emptyFile.value) {
                            await compareSampleAndFile(R.clone(uploadedFile), FileType.JSON);
                        }
                        await filetypeValidation('JSON', uploadedFile);
                        break;
                    }
                    case FileType.XML: {
                        await checkInvalidXML(R.clone(uploadedFile));
                        if (!invalidFormat.value && !emptyFile.value) {
                            await compareSampleAndFile(uploadedFile, FileType.XML);
                        }
                        await filetypeValidation('XML', uploadedFile);
                        break;
                    }
                    case FileType.CSV: {
                        validating.value = true;
                        emit('files-changed', { dataFile: null });
                        await checkInvalidCSV(uploadedFile);
                        if (!invalidFormat.value && !emptyFile.value) {
                            await compareSampleAndFile(uploadedFile, FileType.CSV);
                        }
                        await filetypeValidation('CSV', uploadedFile);
                        break;
                    }
                    case FileType.Parquet: {
                        await checkInvalidParquet(uploadedFile);
                        await filetypeValidation('parquet', uploadedFile);
                        break;
                    }
                    default:
                        emit('files-changed', { dataFile: uploadedFile });
                }
            } else {
                (root as any).$toastr.e(`File with filname "${filename} already uploaded!`, 'Error');
                emit('files-changed', { dataFile: null });
            }
            await validate();
            emit('set-loading', false);
        };

        const nextTab = () => {
            if (props.task.configuration.fileType === FileType.Parquet && !props.task.processedSample?.length) {
                emit('run-on-sample');
            } else emit('next-tab');
        };

        watch(
            () => relativeFileName.value,
            (relativePath: string) => {
                emit('update-task', {
                    ...props.task,
                    configuration: {
                        ...props.task.configuration,
                        inputFilename: buildPath(relativePath, uploadsPath.value),
                    },
                });
            },
        );

        return {
            formatBytes,
            WorkflowStatus,
            acceptedFiles,
            FileType,
            filesValidationRef,
            inDraftStatus,
            inUpdateStatus,
            errorAlert,
            setErrorAlert,
            clearErrorAlert,
            validate,
            clearActualFile,
            fileBlockInfo,
            formatHint,
            showDatasetBlock,
            showDatasetUpload,
            fileUploaded,
            sampleFile,
            uploadFile,
            valPercentage,
            showValidationBar,
            validating,
            nextTab,
            allowNext,
            buildPath,
            uploadsPath,
            uploadsPathWidthInPx,
            R,
            relativeFileName,
        };
    },
});
